
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppLayout from '@/components/layout/AppLayout';
import { ReportList } from './components/ReportList';
import { ReportTabs } from './components/ReportTabs';
import { ReportsHeader } from './components/ReportsHeader';
import { useReports } from './hooks/useReports';
import { Card } from '@/components/ui/card';
import { TabData } from './types';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useClientTeams } from '@/hooks/useClientTeams';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Plus } from 'lucide-react';
import { NoForms } from './components/jotform/NoForms';
import { IncidentLinkDialog } from './components/jotform/IncidentLinkDialog';
import { IncidentReportView } from './components/jotform/IncidentReportView';
import { AddReportDialog } from './components/AddReportDialog';
import { PatrolObservationsView } from './components/patrol/PatrolObservationsView';
import { PatrolLogsView } from './components/patrol/PatrolLogsView';
import { EmergencyReportsView } from './components/emergency/EmergencyReportsView';
import { SupervisorReportsView } from './components/supervisor/SupervisorReportsView';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const TeamReport = () => {
  const { teamId } = useParams();
  const [activeTab, setActiveTab] = useState('reports');
  const { isAdmin, isManager } = useUserRoles();
  const { isClient } = useClientTeams();
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  const { reports, refetchReports } = useReports({ teamId });
  const [showReportView, setShowReportView] = useState(false);

  const handleBackToReports = () => {
    navigate('/reports');
  };

  const handleAddReport = async (data: any) => {
    if (!teamId) return;
    
    try {
      const reportData: {
        title: string;
        content: string;
        team_id: string;
        submitted_at: string;
        image_url?: string;
      } = {
        title: data.title,
        content: data.link,
        team_id: teamId,
        submitted_at: new Date().toISOString()
      };
      
      if (data.imageFile) {
        const fileExt = data.imageFile.name.split('.').pop();
        const fileName = `${crypto.randomUUID()}.${fileExt}`;
        const filePath = `report_images/${fileName}`;
        
        const { error: uploadError } = await supabase.storage
          .from('reports')
          .upload(filePath, data.imageFile);
          
        if (uploadError) {
          throw uploadError;
        }
        
        const { data: urlData } = supabase
          .storage
          .from('reports')
          .getPublicUrl(filePath);
          
        reportData.image_url = urlData.publicUrl;
      }
      
      const { error } = await supabase
        .from('reports')
        .insert(reportData);
        
      if (error) throw error;
      
      toast.success(t("reportAdded") || "Report added successfully");
      refetchReports();
      
    } catch (error: any) {
      console.error('Error adding report:', error);
      toast.error(error.message || "Failed to add report");
    }
  };

  const tabs: TabData[] = [
    { id: 'reports', label: 'Custom Reports' },
    { id: 'incidents', label: 'Custom Replies' },
    { id: 'emergency', label: 'Emergency Reports' },
    { id: 'observations', label: 'Patrol Observations' },
    { id: 'patrol-logs', label: 'Patrol Logs' },
    ...(isAdmin || isManager || isClient ? [{ id: 'supervisor', label: 'Supervisor Reports' }] : [])
  ];

  return (
    <AppLayout>
      <div className="space-y-8 animate-fadeIn px-8 py-8">
        <div className="flex justify-between items-center">
          <ReportsHeader teamId={teamId} onReportsAdded={refetchReports} />
        </div>
        
        <div className="flex gap-3 items-center mb-4">
          <Button 
            variant="outline" 
            onClick={handleBackToReports}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="h-4 w-4" />
            <span>{t("backToReports") || "Back to Reports / Επιστροφή στις αναφορές"}</span>
          </Button>
          
          <div className="flex gap-2">
            {isAdmin && (
              <AddReportDialog onSubmit={handleAddReport} disabled={!teamId} />
            )}
            
            <IncidentLinkDialog teamId={teamId} />
          </div>
        </div>
        
        <div className="flex justify-between items-center">
          <ReportTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
        </div>
        
        <Card className="shadow-sm min-h-[calc(100vh-280px)]">
          {activeTab === 'reports' && (
            <ReportList
              reports={reports || []}
              isLoading={false}
              isSubmissions={false}
              teamId={teamId}
              onReportUpdated={refetchReports}
            />
          )}
          
          {activeTab === 'incidents' && (
            <div className="h-full">
              {teamId ? (
                <IncidentReportView teamId={teamId} />
              ) : (
                <NoForms 
                  isAdmin={isAdmin}
                  teamId={teamId}
                />
              )}
            </div>
          )}

          {activeTab === 'emergency' && (
            <div className="h-full">
              {teamId && <EmergencyReportsView teamId={teamId} />}
            </div>
          )}

          {activeTab === 'observations' && (
            <div className="h-full">
              {teamId && <PatrolObservationsView teamId={teamId} />}
            </div>
          )}

          {activeTab === 'patrol-logs' && (
            <div className="h-full">
              {teamId && <PatrolLogsView teamId={teamId} />}
            </div>
          )}

          {activeTab === 'supervisor' && (isAdmin || isManager || isClient) && (
            <div className="min-h-0">
              {teamId && <SupervisorReportsView teamId={teamId} />}
            </div>
          )}
        </Card>
      </div>
    </AppLayout>
  );
};

export default TeamReport;
